import React, { Component, Fragment } from 'react'
import { inject, observer } from 'mobx-react'

import {
  AbandonExamModal,
  Button,
  FeedbackModal,
  Heading,
  Loader,
  Modal,
} from '@/components'
import { EXAM_ANSWER_TYPE_TIMEOUT } from '@/stores/models/exam'
import request from '@/utils/request'

import OralExamActivity from './OralExamActivity'
import OralExamActivityFeedback from './OralExamActivityFeedback'
import OralExamComplete from './OralExamComplete'
import useAudioRecorder from './useAudioRecorder'

import './OralExam.scss'

const DEFAULT_TIME_PER_QUESTION = 300

// Functional wrapper to provide the useAudioRecorder hook to the class component
const OralExamWithRecorder = (props) => {
  const recorder = useAudioRecorder()
  return <OralExamInner {...props} recorder={recorder} />
}

@inject('contentStore', 'uiStore')
@observer
class OralExamInner extends Component {
  constructor(props) {
    super(props)
    this.state = {
      // View state
      view: 'activity', // 'activity' | 'feedback' | 'complete'
      isAbandonModalOpen: false,

      // Loading states
      isTranscribing: false,
      isSubmitting: false,

      // Transcription
      transcription: '',
      transcriptionError: null,

      // Timer
      timeRemaining: DEFAULT_TIME_PER_QUESTION,
      isTimerRunning: false,
      isTimerExpired: false,
      isAutoSubmitting: false,
      showTimeoutModal: false,
    }

    this.timerIntervalRef = null
    this.timeoutHandledRef = false
    this.autoSubmitPendingRef = false
    this.lastBlobRef = null
    this.timerWasRunningRef = false
  }

  get exam() {
    return this.props.exam
  }

  get courseId() {
    return this.exam?.courseId
  }

  get isPractice() {
    return this.props.isPractice
  }

  get currentQuestion() {
    const { numExamActivitiesTotal, numExamActivitiesRemaining } =
      this.exam || {}
    if (!numExamActivitiesTotal) return 1
    return numExamActivitiesTotal - numExamActivitiesRemaining + 1
  }

  componentDidMount() {
    this.exam.loadActivity().then(() => {
      if (!this.exam.activity && this.exam.isDone) {
        this.setState({ view: 'complete' })
      } else if (!this.exam.activityAudioSrc) {
        this.startTimer()
      }
    })
  }

  componentWillUnmount() {
    this.stopTimer()
  }

  // ──────────────────────────────────────────────
  // API calls
  // ──────────────────────────────────────────────

  transcribeAudio = async (audioBlob) => {
    this.setState({
      isTranscribing: true,
      transcriptionError: null,
    })

    // Pause timer during transcription
    this.timerWasRunningRef =
      this.state.isTimerRunning || !!this.timerIntervalRef
    if (this.timerIntervalRef) {
      clearInterval(this.timerIntervalRef)
      this.timerIntervalRef = null
    }
    this.setState({ isTimerRunning: false })

    try {
      // Convert blob to base64
      const { activity } = this.exam || {}
      const { id: currentActivityId } = activity || {}
      const reader = new FileReader()
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(audioBlob)
      })
      const base64Audio = await base64Promise

      const response = await request.post(
        `/activities/${this.courseId}/practice-oral-exam/transcribe`,
        {
          audio: base64Audio,
          current_activity_id: currentActivityId,
          filename: 'audio.webm',
        },
      )

      const { data } = response.data || response
      const transcription = data?.transcription || ''

      this.setState({ transcription, isTranscribing: false })

      // Resume timer if it was running
      if (this.timerWasRunningRef) {
        this.timerWasRunningRef = false
        this.startTimer()
      }

      return transcription
    } catch (error) {
      console.error('[OralExam] Transcription error:', error)
      this.setState({
        isTranscribing: false,
        transcriptionError: 'Failed to transcribe audio. Please try again.',
      })

      // Resume timer even on error
      if (this.timerWasRunningRef) {
        this.timerWasRunningRef = false
        this.startTimer()
      }

      throw error
    }
  }

  submitAnswer = async (answer, answerType = null) => {
    this.setState({ isSubmitting: true })
    this.stopTimer()

    try {
      await this.exam.saveActivity(answer, answerType)
    } catch (error) {
      console.error('[OralExam] Submit error:', error)
      this.setState({ isSubmitting: false })
      return
    }

    this.setState({ isSubmitting: false })

    if (this.exam.isShowingFeedback) {
      this.setState({ view: 'feedback' })
    } else if (!this.exam.activity || this.exam.isDone) {
      this.setState({ view: 'complete' })
    } else {
      this.resetQuestionState()
      if (!this.exam.activityAudioSrc) {
        this.startTimer()
      }
    }
  }

  // ──────────────────────────────────────────────
  // Timer management
  // ──────────────────────────────────────────────

  startTimer = () => {
    if (this.state.view === 'feedback') return
    if (this.state.isTimerRunning || this.timerIntervalRef) return

    this.setState({ isTimerRunning: true })
    this.timeoutHandledRef = false

    this.timerIntervalRef = setInterval(() => {
      this.setState((prev) => {
        if (prev.timeRemaining <= 1) {
          this.handleTimeout()
          return {
            timeRemaining: 0,
            isTimerExpired: true,
          }
        }
        return { timeRemaining: prev.timeRemaining - 1 }
      })
    }, 1000)
  }

  stopTimer = () => {
    this.setState({ isTimerRunning: false })
    if (this.timerIntervalRef) {
      clearInterval(this.timerIntervalRef)
      this.timerIntervalRef = null
    }
  }

  resetQuestionState = () => {
    const { recorder } = this.props
    if (recorder) recorder.resetRecording()

    this.setState({
      transcription: '',
      transcriptionError: null,
      showTimeoutModal: false,
      isAutoSubmitting: false,
      isSubmitting: false,
      isTimerExpired: false,
      timeRemaining: DEFAULT_TIME_PER_QUESTION,
    })

    this.stopTimer()
    this.lastBlobRef = null
    this.autoSubmitPendingRef = false
    this.timeoutHandledRef = false
    this.timerWasRunningRef = false
  }

  // ──────────────────────────────────────────────
  // Event handlers
  // ──────────────────────────────────────────────

  handleAudioPauseOrEnd = () => {
    this.startTimer()
  }

  handleRecord = async () => {
    const { recorder } = this.props
    const { isAutoSubmitting, isSubmitting, isTranscribing } = this.state

    if (isAutoSubmitting || isSubmitting || isTranscribing) return

    if (recorder.isRecording) {
      try {
        const blob = await recorder.stopRecording()
        if (blob) {
          await this.transcribeBlob(blob, false)
        }
      } catch (error) {
        console.error('Error stopping recording:', error)
      }
      return
    }

    if (this.state.transcription) {
      recorder.resetRecording()
      this.lastBlobRef = null
    }

    try {
      await recorder.startRecording()
    } catch {
      // Error is handled by useAudioRecorder hook
    }
  }

  handleSubmit = async () => {
    this.setState({ isSubmitting: true })
    this.stopTimer()
    await this.submitAnswer(this.state.transcription || null)
  }

  handleTimeout = async () => {
    if (this.timeoutHandledRef) return
    this.timeoutHandledRef = true

    const { recorder } = this.props

    this.setState({
      showTimeoutModal: true,
      isAutoSubmitting: true,
      isSubmitting: true,
    })
    this.stopTimer()

    if (recorder.isRecording) {
      this.autoSubmitPendingRef = true
      try {
        const blob = await recorder.stopRecording()
        if (blob) {
          await this.transcribeBlob(blob, true)
        } else {
          await this.submitAnswer(null, EXAM_ANSWER_TYPE_TIMEOUT)
        }
      } catch (error) {
        console.error('Error stopping recording on timeout:', error)
        await this.submitAnswer(null, EXAM_ANSWER_TYPE_TIMEOUT)
      }
    } else if (this.state.isTranscribing) {
      this.autoSubmitPendingRef = true
    } else {
      await this.submitAnswer(
        this.state.transcription || null,
        EXAM_ANSWER_TYPE_TIMEOUT,
      )
    }
  }

  transcribeBlob = async (blob, isAutoSubmit = false) => {
    if (blob === this.lastBlobRef) return
    this.lastBlobRef = blob

    try {
      const transcribedText = await this.transcribeAudio(blob)
      if (isAutoSubmit) {
        await this.submitAnswer(transcribedText, EXAM_ANSWER_TYPE_TIMEOUT)
      }
    } catch (error) {
      console.error('Transcription error:', error)
      if (isAutoSubmit) {
        await this.submitAnswer(null, EXAM_ANSWER_TYPE_TIMEOUT)
      }
    }
  }

  goToNextQuestion = () => {
    if (this.exam.isDone || this.exam.numExamActivitiesRemaining <= 0) {
      this.setState({ view: 'complete' })
      return
    }

    this.resetQuestionState()
    this.setState({ view: 'activity' }, () => {
      if (this.exam.hasNextActivity) {
        this.exam.advanceFromFeedback()
        if (!this.exam.activityAudioSrc) {
          this.startTimer()
        }
      } else {
        this.exam.loadActivity().then(() => {
          if (!this.exam.activityAudioSrc) {
            this.startTimer()
          }
        })
      }
    })
  }

  onClickAbandonExam = () => {
    this.setState({ isAbandonModalOpen: true })
  }

  onCloseAbandonModal = () => {
    this.setState({ isAbandonModalOpen: false })
  }

  onConfirmAbandonExam = () => {
    this.setState({ isAbandonModalOpen: false })
    this.exam.abandonExam('abandoned').then(() => {
      this.setState({ view: 'complete' })
    })
  }

  onClickBack = () => {
    const { onClickBack } = this.props
    if (onClickBack) onClickBack()
  }

  onOpenFeedbackModal = (activityId, feedback) => {
    const { uiStore } = this.props
    uiStore.openFeedbackModal(feedback)
  }

  onCloseFeedbackModal = () => {
    const { uiStore } = this.props
    uiStore.closeFeedbackModal()
  }

  // ──────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────

  renderFeedbackMobile() {
    const { activity } = this.exam || {}
    const { id: activityId } = activity || {}

    if (!activityId) return null

    return (
      <div className="oral-exam__feedback-buttons oral-exam__feedback-buttons--is-mobile">
        <Button isText onClick={() => this.onOpenFeedbackModal(activityId)}>
          Leave Feedback
        </Button>
      </div>
    )
  }

  renderFeedback() {
    const { uiStore } = this.props
    const { isMobile } = uiStore
    const { activity } = this.exam || {}
    const { id: activityId } = activity || {}

    if (isMobile) return this.renderFeedbackMobile()

    return (
      <div className="oral-exam__user-feedback-buttons-wrapper">
        <Heading className="oral-exam__user-feedback-heading" is4>
          Leave Feedback:
        </Heading>
        <div className="oral-exam__user-feedback-buttons">
          <Button
            className="oral-exam__user-feedback-button"
            isDanger
            onClick={() => this.onOpenFeedbackModal(activityId, 'negative')}
          >
            <i className="oral-exam__user-feedback-icon fa fa-thumbs-down" />
          </Button>
          <Button
            className="oral-exam__user-feedback-button"
            isSuccess
            onClick={() => this.onOpenFeedbackModal(activityId, 'positive')}
          >
            <i className="oral-exam__user-feedback-icon fa fa-thumbs-up" />
          </Button>
        </div>
      </div>
    )
  }

  renderActivityScreen() {
    const { recorder } = this.props
    const {
      isAutoSubmitting,
      isSubmitting,
      isTimerExpired,
      isTranscribing,
      showTimeoutModal,
      timeRemaining,
      transcription,
      transcriptionError,
    } = this.state
    const { activity, activityAudioSrc, numExamActivitiesTotal } =
      this.exam || {}

    return (
      <Fragment>
        <div className="oral-exam__progress">
          <p>
            Question {this.currentQuestion} of {numExamActivitiesTotal}
          </p>
        </div>
        <OralExamActivity
          isAutoSubmitting={isAutoSubmitting}
          isRecorderRecording={recorder.isRecording}
          isRecorderSupported={recorder.isSupported}
          isSubmitted={false}
          isSubmitting={isSubmitting}
          isTimerExpired={isTimerExpired}
          isTranscribing={isTranscribing}
          onEnded={this.handleAudioPauseOrEnd}
          onPause={this.handleAudioPauseOrEnd}
          onRecord={this.handleRecord}
          question={activity}
          questionAudioSrc={activityAudioSrc}
          recordingError={recorder.error}
          showTimeoutModal={showTimeoutModal}
          timeRemaining={timeRemaining}
          transcribedText={transcription}
          transcriptionError={transcriptionError}
        />
        <div className="oral-exam__submit">
          {this.renderFeedback()}
          <Button
            className="oral-exam__abandon-button"
            disabled={isSubmitting || isAutoSubmitting}
            onClick={this.onClickAbandonExam}
          >
            Abandon Exam
          </Button>
          <Button
            disabled={
              isAutoSubmitting ||
              isSubmitting ||
              isTranscribing ||
              recorder.isRecording ||
              !transcription
            }
            isInfo
            isRounded
            onClick={this.handleSubmit}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </Fragment>
    )
  }

  renderFeedbackScreen() {
    const { transcription } = this.state
    const {
      activity,
      activityAudioSrc,
      numExamActivitiesTotal,
      validationResponse,
    } = this.exam || {}

    const isCorrect = validationResponse?.isCorrect ?? null
    const feedback = validationResponse?.feedback
      ? Array.isArray(validationResponse.feedback)
        ? validationResponse.feedback
        : [validationResponse.feedback]
      : null

    return (
      <OralExamActivityFeedback
        audioUrl={activityAudioSrc || ''}
        currentQuestion={this.currentQuestion}
        feedback={feedback}
        isCorrect={isCorrect}
        onEnded={this.handleAudioPauseOrEnd}
        onNext={this.goToNextQuestion}
        onPause={this.handleAudioPauseOrEnd}
        question={activity}
        totalQuestions={numExamActivitiesTotal}
        transcribedText={transcription || ''}
      />
    )
  }

  renderCompleteScreen() {
    const { passed, results, score } = this.exam || {}

    return (
      <OralExamComplete
        isPractice={this.isPractice}
        onClickBack={this.onClickBack}
        passed={passed}
        results={results}
        score={score}
      />
    )
  }

  render() {
    const { uiStore } = this.props
    const { isAbandonModalOpen, view } = this.state
    const { activity, isLoadingActivity } = this.exam || {}
    const { id: activityId } = activity || {}
    const { feedbackModalType, isMobile, isOpenFeedbackModal } = uiStore

    return (
      <Fragment>
        <Modal className="OralExam" isOpen overlayClassName="OralExam__overlay">
          <div className="oral-exam">
            {isLoadingActivity ? (
              <div className="oral-exam__loading">
                <Loader />
              </div>
            ) : view === 'complete' ? (
              this.renderCompleteScreen()
            ) : view === 'feedback' ? (
              this.renderFeedbackScreen()
            ) : (
              this.renderActivityScreen()
            )}
          </div>
        </Modal>
        <AbandonExamModal
          isOpen={isAbandonModalOpen}
          onClose={this.onCloseAbandonModal}
          onConfirm={this.onConfirmAbandonExam}
        />
        <FeedbackModal
          activityId={activityId}
          additionalFeedbackMessage="Additional Comment (Optional)"
          feedbackType={feedbackModalType}
          isExam
          isMobile={isMobile}
          isOralExam
          isOpen={isOpenFeedbackModal}
          label="Leave Feedback"
          onClose={this.onCloseFeedbackModal}
        />
      </Fragment>
    )
  }
}

export default OralExamWithRecorder
